import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router'
import { PiUserSwitch } from 'react-icons/pi'
import { FaExclamationTriangle, FaLock, FaEnvelope, FaQuestionCircle } from 'react-icons/fa'
import { Avatar, Button, Menu, MenuItem, Box, Text, Stack, Icon, UserChip, SearchableSelect, Badge, ImageIcon, Heading, MenuSeparator, NavBubble } from '@/components/lib'
import { useColorMode } from '@/components/ui/color-mode'
import api from '../../api'
import useStore from '../../store'
import utils from '../../utils/utils.js'

import LogoLight from '../../images/logo/icon.png'
import SimplePopover from '../lib/SimplePopover.jsx'
import TrialSearch from './TrialSearch.jsx'

const LogoLink = ({ user, team, isDark }) => {
  const imageStyle = { maxWidth: '150px', maxHeight: '40px' }
  // Only show logo once team loads
  if (!user && !team) return null
  if (user?.team && !team) return null

  let src = LogoLight
  let alt = 'Trialflare icon'

  if (team) {
    if (!isDark && team.logoUrl) {
      src = team.logoUrl
      alt = `${team.name} logo`
    } else if (isDark && team.darkLogoUrl) {
      src = team.darkLogoUrl
      alt = `${team.name} logo`
    }
  }

  return (
    <Box as={Link} textAlign='center' to={user ? '/trials' : '/'} display='flex' justifyContent='center' alignItems='center'>
      <img src={src} style={imageStyle} alt={alt} />
    </Box>
  )
}

export default function NavBar () {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, team, completeLogout, trial, trials } = useStore()
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'

  const isTrialRoute = /^\/trials\/[^/]+/.test(location.pathname)
  const searchValue = isTrialRoute ? trial?._id : null
  const [availableTeams, setAvailableTeams] = useState([])

  useEffect(() => {
    if (user?.teams?.length > 0) {
      api.teams.getAll(data => setAvailableTeams(data?.teams || []))
    }
  }, [user?._id])

  function logout () {
    api.accounts.logout(() => {
      completeLogout()
      navigate('/')
    })
  }

  function switchTeam (teamId) {
    api.users.updateTeam(user._id, teamId, () => {
      window.location.href = '/trials'
    })
  }

  const otherTeams = availableTeams.filter(t => t._id !== team?._id)

  return (
    <Box bg='bg.canvas'>
      <div style={{ height: 70, width: '100%', padding: '15px 15px' }}>
        <Stack direction='row' gap={3} justifyContent='space-between' width='100%' style={{ overflow: 'hidden' }} alignItems='center'>
          <Stack direction='row' alignItems='center' gap={3}>
            <LogoLink user={user} team={team} isDark={isDark} />
            <Stack direction='row' alignItems='center' gap={2}>
              {(trials.length > 0 || utils.hasPermission(user, 'trials.create', team?._id)) &&
                <SearchableSelect
                  maxW='400px'
                  variant='ghost'
                  ellipsize
                  value={searchValue}
                  onChange={value => navigate(`/trials/${value}/manage`)}
                  options={trials.map(t => ({ label: t.name, value: t._id }))}
                  placeholder='Choose a project...'
                  action={utils.hasPermission(user, 'trials.create', team?._id) && <Button size='sm' w='100%' colorPalette='primary' onClick={() => navigate('/trials/new')}>Create a project</Button>}
                />}
              {trial && <TrialSearch />}

              {trial && utils.canManageTrialWallet(user, trial) && (trial.walletBalance ?? 0) < 0 &&
                <SimplePopover
                  trigger={<NavBubble icon={FaExclamationTriangle} status='warning' />}
                  body={
                    <Stack gap={3}>
                      <Stack direction='row'><ImageIcon iconKey='wallet' size={50} /></Stack>
                      <Heading size='md'>Trial wallet is overdrawn</Heading>
                      <Text>The project wallet balance is negative. Add funds in Settings → Wallet so messaging and paid features can keep running.</Text>
                      <Button as={Link} to={`/trials/${trial._id}/settings/wallet`}>Open wallet</Button>
                    </Stack>
                  }
                />}
              {(trial?.isLocked || trial?.isDatabaseLocked) &&
                <SimplePopover
                  trigger={<NavBubble icon={FaLock} status='info' />}
                  body={
                    <Stack gap={3}>
                      <Stack direction='row'><ImageIcon iconKey='lock' size={50} /></Stack>
                      <Heading size='md'>Locks</Heading>
                      <Stack direction='row' gap={2}>
                        {trial?.isLocked && <Badge colorPalette='green'>Trial is locked</Badge>}
                        {trial?.isDatabaseLocked && <Badge colorPalette='green'>Database is locked</Badge>}
                      </Stack>
                      <Text>Locks are used to prevent changes to the trial configuration or the results database.</Text>
                      <Button as={Link} to={`/trials/${trial._id}/settings/locks`}>Manage locks</Button>
                    </Stack>
}
                />}
              {trial?.unreadMessages > 0 &&
                <SimplePopover
                  trigger={<NavBubble icon={FaEnvelope} status='info' badge={trial.unreadMessages} />}
                  body={
                    <Stack gap={3}>
                      <Stack direction='row'><ImageIcon iconKey='messages' size={50} /></Stack>
                      <Heading size='md'>Unread messages</Heading>
                      <Text>There {trial.unreadMessages > 1 ? 'are' : 'is'} {trial.unreadMessages} unread participant message{trial.unreadMessages > 1 ? 's' : ''}.</Text>
                      <Button as={Link} to={`/trials/${trial._id}/messages`}>View messages</Button>
                    </Stack>
}
                />}
            </Stack>
          </Stack>
          <Stack direction='row' alignItems='center'>
            {user?.hasPassword && !user?.otpEnabled && !user?.hasPasskey && (utils.hasPermission(user, 'team.access', team?._id) || utils.hasPermission(user, 'team.manage', team?._id) || utils.canManageTrial(user, trial)) &&
              <SimplePopover
                trigger={<NavBubble icon={FaExclamationTriangle} status='warning' />}
                body={
                  <Stack gap={3}>
                    <Stack direction='row'><ImageIcon iconKey='mfa' size={50} /></Stack>
                    <Heading size='md'>Your account has no two-factor authentication</Heading>
                    <Text>Two-factor authentication (2FA) is a security feature that adds an extra layer of security to your account. We recommend it for all users.</Text>
                    <Button as={Link} to='/settings/security'>Set up 2FA</Button>
                  </Stack>
                }
              />}
            {user &&
              <Menu
                trigger={<Box cursor='pointer'><Avatar size='sm' name={user?.firstName} /></Box>}
              >
                <Stack gap={3}>
                  <Box bg='bg.subtle' rounded='md' overflow='clip'>
                    <Box p={3}>
                      <UserChip user={user} vertical />
                    </Box>
                  </Box>
                  <Box>
                    <MenuItem value='settings' as={Link} to='/settings'>Account settings</MenuItem>
                    <MenuItem value='logout' onClick={logout}>Logout</MenuItem>
                    {team && utils.hasPermission(user, 'team.manage', team?._id) &&
                      <>
                        <MenuSeparator />
                        <MenuItem value='team' as={Link} to='/team'>Team settings</MenuItem>
                      </>}

                    <MenuSeparator />
                    <MenuItem value='help' as={Link} to='https://help.trialflare.com' target='_blank'><Icon as={FaQuestionCircle} /> Help Centre</MenuItem>

                    {utils.hasPermission(user, 'root') &&
                      <MenuItem value='root' asChild><Link to='/root'>Root account</Link></MenuItem>}

                    {otherTeams.length > 0 &&
                      <>
                        <MenuSeparator />
                        {otherTeams.map(t => (
                          <MenuItem key={t._id} value={`team-${t._id}`} onClick={() => switchTeam(t._id)}>
                            <Icon as={PiUserSwitch} /> {t.name}
                          </MenuItem>
                        ))}
                      </>}
                  </Box>
                </Stack>
              </Menu>}
          </Stack>
        </Stack>
      </div>
    </Box>
  )
}
